import React, { useEffect, useState } from 'react';
import { Download, FileText, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function Relatorios() {
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [crmCards, setCrmCards] = useState([]);

  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [contractsRes, paymentsRes, expensesRes, clientsRes, crmRes] = await Promise.all([
        api.get('/contracts'),
        api.get('/payments'),
        api.get('/expenses'),
        api.get('/clients'),
        api.get('/leads')
      ]);

      setContracts(contractsRes.data);
      setPayments(paymentsRes.data);
      setExpenses(expensesRes.data);
      setClients(clientsRes.data);
      setCrmCards(crmRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'N/A';
  };

  const filterByDate = (items, dateField) => {
    if (!startDate && !endDate) return items;

    return items.filter(item => {
      const itemDate = new Date(item[dateField]);
      const start = startDate ? new Date(startDate) : new Date('2000-01-01');
      const end = endDate ? new Date(endDate) : new Date('2100-01-01');
      return itemDate >= start && itemDate <= end;
    });
  };

  const generatePDFReport = () => {
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text('Relatório Kode Studio', 14, 20);

    doc.setFontSize(12);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 14, 30);

    if (startDate || endDate) {
      doc.text(`Período: ${startDate || 'Início'} até ${endDate || 'Fim'}`, 14, 38);
    }

    let yPos = 50;

    doc.setFontSize(14);
    doc.text('Resumo Financeiro', 14, yPos);
    yPos += 10;

    const totalPago = payments
      .filter(p => p.status === 'pago')
      .reduce((sum, p) => sum + p.value, 0);

    const totalDespesas = expenses.reduce((sum, e) => sum + e.value, 0);
    const lucro = totalPago - totalDespesas;

    doc.setFontSize(11);
    doc.text(`Faturamento: ${formatCurrency(totalPago)}`, 14, yPos);
    yPos += 8;
    doc.text(`Despesas: ${formatCurrency(totalDespesas)}`, 14, yPos);
    yPos += 8;
    doc.text(`Lucro: ${formatCurrency(lucro)}`, 14, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.text('Contratos Ativos', 14, yPos);
    yPos += 10;

    const activeContracts = contracts.filter(c => c.status === 'ativo');
    doc.setFontSize(11);
    doc.text(`Total: ${activeContracts.length}`, 14, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.text('Pipeline CRM', 14, yPos);
    yPos += 10;

    const pipelineCounts = {};
    crmCards.forEach(card => {
      pipelineCounts[card.status] = (pipelineCounts[card.status] || 0) + 1;
    });

    doc.setFontSize(11);
    Object.entries(pipelineCounts).forEach(([status, count]) => {
      doc.text(`${status}: ${count}`, 14, yPos);
      yPos += 8;
    });

    doc.save('relatorio-kode-studio.pdf');
    toast.success('Relatório PDF gerado!');
  };

  const getCSVData = () => {
    const filteredPayments = filterByDate(payments, 'due_date');
    const filteredExpenses = filterByDate(expenses, 'date');

    return [
      ['RESUMO FINANCEIRO'],
      [''],
      ['Tipo', 'Cliente', 'Valor', 'Data', 'Status'],
      ...filteredPayments.map(p => [
        'Recebimento',
        getClientName(p.client_id),
        p.value,
        formatDate(p.due_date),
        p.status
      ]),
      ...filteredExpenses.map(e => [
        'Despesa',
        e.category,
        -e.value,
        formatDate(e.date),
        'Pago'
      ])
    ];
  };

  const totalReceived = payments
    .filter(p => p.status === 'pago')
    .reduce((sum, p) => sum + p.value, 0);

  const totalExpenses = expenses.reduce((sum, e) => sum + e.value, 0);
  const profit = totalReceived - totalExpenses;

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Relatórios</h1>
        <p className="text-slate-600 mt-1">Visualize e exporte relatórios completos</p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Tipo de Relatório</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="financial">Financeiro</SelectItem>
                <SelectItem value="contracts">Contratos</SelectItem>
                <SelectItem value="crm">CRM</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="start-date">Data Início</Label>
            <Input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="start-date-input"
            />
          </div>
          <div>
            <Label htmlFor="end-date">Data Fim</Label>
            <Input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="end-date-input"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-success" />
            </div>
            <p className="text-sm font-medium text-slate-500 uppercase">Faturamento</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalReceived)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-error/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-error" />
            </div>
            <p className="text-sm font-medium text-slate-500 uppercase">Despesas</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalExpenses)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm font-medium text-slate-500 uppercase">Lucro</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(profit)}</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Estatísticas Gerais</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">Total de Clientes</p>
            <p className="text-3xl font-bold text-slate-900">{clients.length}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Contratos Ativos</p>
            <p className="text-3xl font-bold text-slate-900">
              {contracts.filter(c => c.status === 'ativo').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Total CRM Cards</p>
            <p className="text-3xl font-bold text-slate-900">{crmCards.length}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Taxa de Conversão</p>
            <p className="text-3xl font-bold text-slate-900">
              {crmCards.length > 0
                ? `${((crmCards.filter(c => c.status === 'fechado').length / crmCards.length) * 100).toFixed(1)}%`
                : '0%'
              }
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Exportar Relatórios</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={generatePDFReport}
            className="bg-primary hover:bg-primary/90"
            data-testid="export-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>

          <CSVLink
            data={getCSVData()}
            filename={`relatorio-kode-studio-${new Date().toISOString().split('T')[0]}.csv`}
            className="inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors h-10 px-4 py-2 bg-secondary hover:bg-secondary/90 text-white"
            onClick={() => toast.success('Relatório CSV gerado!')}
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </CSVLink>
        </div>
      </div>
    </div>
  );
}